import { datadogRum } from '@datadog/browser-rum';
import { isMobile } from '@lemonade-hq/cdk';
import { ma } from '@lemonade-hq/chat-engine';
import type { SchemaData } from '@lemonade-hq/chat-engine';
import type { OnInvalidAnswer, WizardExtendedStepWithPersistence } from '@lemonade-hq/chat-wizard-client-base';
import type { WizardComponentBase } from '@lemonade-hq/chat-wizard-common-definitions';
import { transformDataBasedOnShapeRecursively } from '@lemonade-hq/maschema-schema';
import type { Shape } from '@lemonade-hq/maschema-schema';
import type { ShapeSecureExtension } from '@lemonade-hq/maschema-schema-secure';
import type { FlatValidationsTree } from '@lemonade-hq/maschema-validations';
import { isCurrentTrackedUser, trackDataForGtm, useUserContext } from '@lemonade-hq/nuiza';
import { devWarn, NOOP } from '@lemonade-hq/ts-helpers';
import type { Maybe } from '@lemonade-hq/ts-helpers';
import type { UniclientBaseFrame, UniclientStep } from '@lemonade-hq/uniclient-chat';
import { isNativeAndroid, isNativeIOS } from '@lemonade-hq/uniclient-mobile-bridge';
import { SHA256 as sha256 } from 'crypto-es/lib/sha256';
import get from 'lodash/get';
import omit from 'lodash/omit';
import type { PropsWithChildren } from 'react';
import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import type { Simplify } from 'type-fest';
import { alias, identify, setSessionRecordingUrlCallback, track } from '../analytics/tracking/helpers';
import type { BaseEventPayload, Client, Event, EventPrefix } from '../analytics/tracking/types';

export const MISSING_PRODUCT = '<MISSING_PRODUCT>';

interface UpdateIdentityProps {
  readonly answers: SchemaData;
  readonly mappedIdentityParams?: Record<string, string>;
  readonly extraIdentityParams?: Record<string, string>;
}

export interface UniclientTrackingContextValue {
  readonly trackError: (errorMessage: string) => void;
  readonly trackAnswers: (answers?: SchemaData) => Promise<void>;
  readonly trackEvent: <TEventName extends Event['name']>(
    name: TEventName,
    payload?: Simplify<Omit<(Event & { readonly name: TEventName })['payload'], keyof BaseEventPayload>>,
  ) => void;
  readonly trackAction: (name: string, customParams?: Record<string, string>) => void;
  readonly trackEdit: (answers?: SchemaData) => Promise<void>;
  readonly trackStepViewed: () => void;
  readonly trackSuccess: () => void;
  readonly updateIdentity: (props: UpdateIdentityProps) => void;
  readonly initTrackingParams: (
    commonEventPayload: BaseEventPayload,
    commonAnalyticsParams: CommonAnalyticsParams,
  ) => void;
  readonly trackInvalidAnswer: OnInvalidAnswer;
  readonly getSecureAnswers: (answers: SchemaData) => Promise<SchemaData>;
}
export interface CommonAnalyticsParams {
  readonly currentStep: Maybe<WizardExtendedStepWithPersistence<UniclientStep>>;
  readonly currentStepAnswers: SchemaData;
  readonly answersSchema: Shape | null;
  readonly isSubmitting: boolean;
  readonly userPublicId?: string;
  readonly eventPrefix: `${Exclude<EventPrefix, 'plaid' | 'section'>}`;
}

export const UniclientTrackingContext = createContext<UniclientTrackingContextValue>({
  trackError: NOOP,
  trackAnswers: async () => {},
  trackEvent: NOOP,
  trackAction: NOOP,
  trackEdit: async () => {},
  trackStepViewed: NOOP,
  trackSuccess: NOOP,
  updateIdentity: NOOP,
  initTrackingParams: NOOP,
  trackInvalidAnswer: NOOP,
  getSecureAnswers: async () => ({}),
});

export function getClientForTracking(): Client {
  if (isNativeIOS()) {
    return 'ios';
  }

  if (isNativeAndroid()) {
    return 'android';
  }

  return isMobile() ? 'mobile_web' : 'web';
}

export const getEventPrefix = (
  stepFrameType: UniclientBaseFrame['type'],
  componentsType?: WizardComponentBase['type'],
): `${Exclude<EventPrefix, 'plaid' | 'section'>}` => {
  if (stepFrameType !== 'page') return 'chat';

  switch (componentsType) {
    case 'checkout':
      return 'checkout';
    case 'cart':
      return 'summary';
    default:
      return 'quote';
  }
};

export function filterIgnoredAnswers(answers: SchemaData, ignoredAttributes: string[]): SchemaData {
  return omit(answers, ignoredAttributes) as SchemaData;
}

export const UniclientTrackingProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { user } = useUserContext();

  const commonEventPayloadRef = useRef<BaseEventPayload>();
  const commonAnalyticsParamsRef = useRef<CommonAnalyticsParams>();
  const sessionRecordingDataRef = useRef<Record<string, string>>(null);

  const initTrackingParams = useCallback(
    (commonEventPayload: BaseEventPayload, commonAnalyticsParams: CommonAnalyticsParams) => {
      commonEventPayloadRef.current = commonEventPayload;
      commonAnalyticsParamsRef.current = commonAnalyticsParams;
      /* istanbul ignore next -- @preserve */
      if (sessionRecordingDataRef.current == null) {
        setSessionRecordingUrlCallback(sessionRecordingDataRef);
      }
    },
    [],
  );

  const trackEvent = useCallback(
    <TEventName extends Event['name']>(
      name: TEventName,
      payload?: Omit<(Event & { name: NoInfer<TEventName> })['payload'], keyof BaseEventPayload>,
    ): void => {
      // @ts-expect-error: weird typing issue with missing error code
      track(name, {
        ...commonEventPayloadRef.current,
        ...sessionRecordingDataRef.current,
        ...payload,
      });
    },
    [],
  ) as UniclientTrackingContextValue['trackEvent'];

  const trackStepViewed = useCallback(() => {
    const currentStep = commonAnalyticsParamsRef.current?.currentStep;
    const identifyParams = currentStep?.analytics?.identifyParams ?? undefined;

    if (
      commonAnalyticsParamsRef.current?.userPublicId != null &&
      !isCurrentTrackedUser(commonAnalyticsParamsRef.current.userPublicId)
    ) {
      alias(commonAnalyticsParamsRef.current.userPublicId);
      identify(commonAnalyticsParamsRef.current.userPublicId, identifyParams);
    } else if (identifyParams != null && Object.keys(identifyParams).length > 0) {
      identify(identifyParams);
    }

    trackEvent(`${commonAnalyticsParamsRef.current?.eventPrefix ?? 'quote'}.viewed`);

    datadogRum.setGlobalContextProperty('script-details', {
      ...commonEventPayloadRef.current,
      product: commonEventPayloadRef.current?.product ?? MISSING_PRODUCT,
    });

    if (commonAnalyticsParamsRef.current?.currentStep?.analytics?.gtmParams != null) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- eventPrefix can be undefined
      trackDataForGtm(`${commonAnalyticsParamsRef.current?.eventPrefix ?? 'quote'}.viewed`, {
        questionAttribute: currentStep?.name ?? '<UNKNOWN>',
        product: commonEventPayloadRef.current?.product ?? MISSING_PRODUCT,
        ...currentStep?.analytics?.gtmParams,
      });
    }
  }, [trackEvent]);

  const getSecureAnswers = useCallback(async (answers: SchemaData) => {
    const transformFunction = async (value: unknown): Promise<string> => {
      try {
        return sha256(JSON.stringify(value)).toString();
      } catch {
        return '<REDACTED>';
      }
    };

    const secureAnswers = await transformDataBasedOnShapeRecursively(
      answers,
      commonAnalyticsParamsRef.current?.answersSchema ?? ma.record({}),
      (shape: Shape & ShapeSecureExtension) => shape.maskedTelemetry ?? shape.secure ?? false,
      transformFunction,
    );

    let secureTransientAnswers: SchemaData = {};
    if (commonAnalyticsParamsRef.current?.currentStep?.data?.transients?.schema != null) {
      secureTransientAnswers = await transformDataBasedOnShapeRecursively(
        answers,
        commonAnalyticsParamsRef.current.currentStep.data.transients.schema,
        (shape: Shape & ShapeSecureExtension) => shape.maskedTelemetry ?? shape.secure ?? false,
        transformFunction,
      );
    }

    return { ...secureAnswers, ...secureTransientAnswers };
  }, []);

  const trackAnswers = useCallback(
    async (answers: SchemaData = {}, type: 'edited' | 'submitted' = 'submitted'): Promise<void> => {
      const ignoredAttributes = commonAnalyticsParamsRef.current?.currentStep?.analytics?.ignoredAttributes ?? [];
      let processedAnswers = await getSecureAnswers(answers);

      if (ignoredAttributes.length > 0) {
        const filteredAnswers = filterIgnoredAnswers(processedAnswers, ignoredAttributes);
        if (Object.keys(filteredAnswers).length === 0) {
          devWarn(
            `All answers were ignored or filtered out. ${JSON.stringify({ answers })}, ${JSON.stringify({
              ignoredAttributes,
            })}`,
          );
          return;
        }

        processedAnswers = filteredAnswers;
      }

      trackEvent(`${commonAnalyticsParamsRef.current?.eventPrefix ?? 'quote'}.${type}`, {
        ...processedAnswers,
        changedFields: Object.keys(processedAnswers),
      });

      trackDataForGtm(`${commonAnalyticsParamsRef.current?.eventPrefix ?? 'quote'}.${type}`, {
        stepName: commonAnalyticsParamsRef.current?.currentStep?.name ?? '<UNKNOWN>',
        product: commonEventPayloadRef.current?.product ?? MISSING_PRODUCT,
        ...processedAnswers,
      });
    },
    [trackEvent, getSecureAnswers],
  );

  const trackError = useCallback(
    (errorCode: string) => {
      const payload = commonAnalyticsParamsRef.current?.isSubmitting
        ? ({ isSubmitting: true, errorCode } as const)
        : { errorCode };
      trackEvent(`${commonAnalyticsParamsRef.current?.eventPrefix ?? 'quote'}.failed`, payload);
    },
    [trackEvent],
  );

  const trackEdit = useCallback(
    async (answers: SchemaData = {}) => {
      await trackAnswers(answers, 'edited');
    },
    [trackAnswers],
  );

  const trackAction = useCallback(
    (name: string, customParams: Record<string, string> = {}) => {
      trackEvent(`${commonAnalyticsParamsRef.current?.eventPrefix ?? 'quote'}.action`, {
        action: name,
        ...customParams,
      });
    },
    [trackEvent],
  );

  const trackInvalidAnswer = useCallback(
    async (message: string, failingValidations?: FlatValidationsTree, answers?: SchemaData) => {
      const secureAnswers = await getSecureAnswers(answers ?? {});
      datadogRum.addAction('invalid_answer', {
        message,
        failingValidations,
        answers: secureAnswers,
      });
      trackEvent(`${commonAnalyticsParamsRef.current?.eventPrefix ?? 'quote'}.invalid_answers`, {
        message,
        failingValidations,
        answers: secureAnswers,
      });
    },
    [trackEvent, getSecureAnswers],
  );

  const trackSuccess = useCallback(() => {
    trackEvent(`${commonAnalyticsParamsRef.current?.eventPrefix ?? 'quote'}.success`);
  }, [trackEvent]);

  const updateIdentity = useCallback(
    ({ answers, mappedIdentityParams, extraIdentityParams }: UpdateIdentityProps) => {
      const mappedIdentityParamsFromAnswers = Object.keys(mappedIdentityParams ?? {}).reduce((result, answerKey) => {
        const mappedKey = mappedIdentityParams![answerKey]; // eslint-disable-line @typescript-eslint/no-non-null-assertion
        return { ...result, [mappedKey]: get(answers, answerKey) as unknown };
      }, {});

      const mergedIdentityParams = { ...extraIdentityParams, ...mappedIdentityParamsFromAnswers };

      if (user?.userId != null) {
        identify(user.userId, mergedIdentityParams);
      } else {
        identify(mergedIdentityParams);
      }
    },
    [user],
  );

  const value = useMemo(
    () => ({
      trackAction,
      trackEvent,
      trackError,
      trackAnswers,
      trackEdit,
      trackStepViewed,
      trackSuccess,
      updateIdentity,
      initTrackingParams,
      trackInvalidAnswer,
      getSecureAnswers,
    }),
    [
      trackAction,
      trackEvent,
      trackError,
      trackAnswers,
      trackEdit,
      trackStepViewed,
      trackSuccess,
      updateIdentity,
      initTrackingParams,
      trackInvalidAnswer,
      getSecureAnswers,
    ],
  );
  return <UniclientTrackingContext.Provider value={value}>{children}</UniclientTrackingContext.Provider>;
};

export function useUniclientTrackingContext(): UniclientTrackingContextValue {
  return useContext(UniclientTrackingContext);
}
